/** Tab Ideeën: weerbalk, filters, suggesties. */
import { CloudRain, Dices, Footprints, FilterX, Heart, RefreshCw, RotateCcw, Shuffle } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { filterActivities } from '../../domain/activities';
import { filterByTime, suggest, surpriseMe, type Suggestion, type SuggestContext } from '../../domain/suggest';
import type { Activity, ActivityFilters } from '../../domain/types';
import { useActivities, useLogs, usePrefs, useTimer, useToast, useWeather } from '../../state/AppProviders';
import { usePersistedTimeBudget } from '../../state/timeBudget';
import { FilterChips } from './FilterChips';
import { SuggestionCard } from './SuggestionCard';
import { SurpriseModal } from './SurpriseModal';
import { WeatherBanner } from './WeatherBanner';

const NO_FILTERS: ActivityFilters = { type: null, energie: null, personen: null };
const COUNT = 5;

const primaryBtn =
  'inline-flex min-h-11 items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-slate-900 px-5 text-sm font-semibold text-slate-50 transition-all duration-150 active:scale-95 hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:focus-visible:outline-slate-100';
const softBtn =
  'inline-flex min-h-11 items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-slate-900/5 px-5 text-sm font-semibold text-slate-800 transition-all duration-150 active:scale-95 hover:bg-slate-900/10 dark:bg-white/10 dark:text-slate-100 dark:hover:bg-white/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:focus-visible:outline-slate-100';

function EmptyState({ icon, title, text, children }: { icon: ReactNode; title: string; text: string; children: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-3xl bg-slate-900/5 px-6 py-10 text-center dark:bg-white/5">
      <div className="flex size-14 items-center justify-center rounded-2xl bg-slate-900/5 text-slate-600 dark:bg-white/10 dark:text-slate-300">{icon}</div>
      <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50">{title}</h3>
      <p className="max-w-sm text-sm text-slate-600 dark:text-slate-300">{text}</p>
      <div className="mt-1 flex flex-wrap justify-center gap-2">{children}</div>
    </div>
  );
}

export default function IdeasTab() {
  const { activities, loading, resetToDefaults } = useActivities();
  const { weather, overruled, setOverruled } = useWeather();
  const { timer, start } = useTimer();
  const { showToast } = useToast();
  const { logs } = useLogs();
  const { favorites, hiddenIds } = usePrefs();
  const [timeBudget, setTimeBudget] = usePersistedTimeBudget();
  const [filters, setFilters] = useState<ActivityFilters>(NO_FILTERS);
  const [onlyFavorites, setOnlyFavorites] = useState(false);
  const [shown, setShown] = useState<Suggestion[]>([]);
  const [surprise, setSurprise] = useState<Suggestion | null>(null);
  const recentRef = useRef<Set<number>>(new Set());

  const candidates = useMemo(() => {
    const visible = activities.filter((a) => !hiddenIds.has(a.id));
    const list = filterByTime(
      filterActivities(visible, filters, { isBadWeather: weather.isBadWeather, weatherOverruled: overruled }),
      timeBudget,
    );
    return onlyFavorites ? list.filter((a) => favorites.has(a.id)) : list;
  }, [activities, hiddenIds, filters, weather.isBadWeather, overruled, timeBudget, onlyFavorites, favorites]);
  const candidatesKey = candidates.map((a) => a.id).join(',');

  // Altijd de nieuwste waarden bij het kiezen, zonder dat logs/favorieten zelf een nieuwe selectie triggeren.
  const latest = useRef({ candidates, logs, favorites, weather, timeBudget });
  latest.current = { candidates, logs, favorites, weather, timeBudget };
  const makeCtx = useCallback((): SuggestContext => {
    const l = latest.current;
    return {
      now: new Date(),
      logs: l.logs,
      favorites: l.favorites,
      weather: { kind: l.weather.kind, isBadWeather: l.weather.isBadWeather, source: l.weather.source },
      recentlyShownIds: recentRef.current,
      timeBudget: l.timeBudget,
    };
  }, []);

  const pick = useCallback(() => {
    const picked = suggest(latest.current.candidates, makeCtx(), COUNT);
    recentRef.current = new Set(picked.map((s) => s.activity.id));
    setShown(picked);
  }, [makeCtx]);

  // Nieuwe selectie bij andere filters / weer / lijst (niet bij alleen een scoreverandering of log).
  useEffect(() => {
    pick();
  }, [candidatesKey, pick]);

  const reshuffle = () => {
    pick();
    showToast('5 nieuwe suggesties geladen');
  };

  const openSurprise = () => setSurprise(surpriseMe(latest.current.candidates, makeCtx()));

  const anotherSurprise = () => {
    const cur = surprise?.activity.id;
    let next = surpriseMe(latest.current.candidates, makeCtx());
    for (let i = 0; i < 6 && next && next.activity.id === cur && latest.current.candidates.length > 1; i++) {
      next = surpriseMe(latest.current.candidates, makeCtx());
    }
    if (next) setSurprise(next);
  };

  const onStart = (a: Activity) => start({ id: a.id, naam: a.naam, duurMinuten: a.duur, type: a.type, energie: a.energie });

  const clearAll = () => {
    setFilters(NO_FILTERS);
    setOnlyFavorites(false);
    setTimeBudget(null);
  };

  const hasOtherFilters = !!(filters.type || filters.energie || filters.personen || timeBudget);
  const hasFilters = hasOtherFilters || onlyFavorites;
  const badOutdoor = weather.isBadWeather && !overruled && filters.type === 'Buiten';
  const noFavoritesYet = onlyFavorites && !hasOtherFilters;

  const reshuffleBtn = (
    <button type="button" onClick={reshuffle} disabled={candidates.length === 0} className={`${softBtn} disabled:pointer-events-none disabled:opacity-50`}>
      <Shuffle size={16} aria-hidden="true" /> Nieuwe suggesties
    </button>
  );

  const surpriseBtn = (
    <button
      type="button"
      onClick={openSurprise}
      disabled={candidates.length === 0}
      aria-describedby={candidates.length === 0 ? 'surprise-hint' : undefined}
      className={`${primaryBtn} disabled:pointer-events-none disabled:opacity-50`}
    >
      <Dices size={18} aria-hidden="true" /> Verras me
    </button>
  );

  let body: ReactNode;
  if (loading && activities.length === 0) {
    body = <p className="py-10 text-center text-sm text-slate-600 dark:text-slate-300">Activiteiten laden…</p>;
  } else if (activities.length === 0) {
    body = (
      <EmptyState icon={<RefreshCw size={26} aria-hidden="true" />} title="Nog geen activiteiten" text="Je lijst is leeg. Laad de standaardactiviteiten om te beginnen.">
        <button type="button" className={primaryBtn} onClick={() => void resetToDefaults()}>
          <RotateCcw size={16} aria-hidden="true" /> Standaardactiviteiten laden
        </button>
      </EmptyState>
    );
  } else if (candidates.length === 0 && badOutdoor) {
    body = (
      <EmptyState icon={<CloudRain size={26} aria-hidden="true" />} title="Buiten is nu niet ideaal" text="Door het weer zijn buitenactiviteiten verborgen. Je kunt toch naar buiten, of liever binnen kijken.">
        <button type="button" className={primaryBtn} onClick={() => setOverruled(true)}>
          <Footprints size={16} aria-hidden="true" /> Toch naar buiten
        </button>
        <button type="button" className={softBtn} onClick={() => setFilters({ ...filters, type: 'Binnen' })}>
          Binnen bekijken
        </button>
      </EmptyState>
    );
  } else if (candidates.length === 0) {
    body = (
      <EmptyState
        icon={noFavoritesYet ? <Heart size={26} aria-hidden="true" /> : <FilterX size={26} aria-hidden="true" />}
        title={noFavoritesYet ? 'Nog geen favorieten' : 'Niets gevonden'}
        text={
          noFavoritesYet
            ? 'Tik op het hartje bij een activiteit om hem hier terug te vinden.'
            : timeBudget
              ? 'Geen activiteit past bij deze filters en de beschikbare tijd.'
              : 'Geen activiteit past bij deze filters.'
        }
      >
        <button type="button" className={primaryBtn} onClick={clearAll}>
          Alle filters wissen
        </button>
      </EmptyState>
    );
  } else {
    body = (
      <>
        <ul className="space-y-4">
          {shown.map((s) => (
            <li key={s.activity.id}>
              <SuggestionCard activity={s.activity} reasons={s.reasons} onStart={onStart} isRunning={timer?.activity.id === s.activity.id} />
            </li>
          ))}
        </ul>
        <div className="flex justify-center pt-2">{reshuffleBtn}</div>
      </>
    );
  }

  return (
    <section aria-label="Ideeën" className="space-y-5">
      <WeatherBanner />

      <div className="space-y-3">
        <div className="min-w-0">
          <h2 className="text-2xl font-extrabold leading-tight text-slate-900 dark:text-slate-50">Wat gaan we doen?</h2>
          <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-300">Vijf ideeën die nu passen.</p>
        </div>
        {activities.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {surpriseBtn}
            {reshuffleBtn}
          </div>
        ) : null}
        {activities.length > 0 && candidates.length === 0 ? (
          <p id="surprise-hint" className="text-sm text-slate-600 dark:text-slate-300">
            Verras me werkt zodra er een activiteit past bij je filters.
          </p>
        ) : null}
      </div>

      <FilterChips
        filters={filters}
        onChange={setFilters}
        timeBudget={timeBudget}
        onTimeBudgetChange={setTimeBudget}
        onlyFavorites={onlyFavorites}
        onOnlyFavoritesChange={setOnlyFavorites}
      />

      {overruled && weather.isBadWeather ? (
        <p className="flex items-center gap-2 rounded-2xl bg-slate-900/5 px-4 py-3 text-sm text-slate-700 dark:bg-white/10 dark:text-slate-200">
          <Footprints size={16} aria-hidden="true" className="shrink-0" />
          <span>Toch naar buiten: buitenactiviteiten worden getoond ondanks het weer.</span>
        </p>
      ) : null}

      {hasFilters && candidates.length > 0 ? (
        <button type="button" onClick={clearAll} className="inline-flex min-h-11 items-center gap-2 whitespace-nowrap text-sm font-semibold text-slate-600 underline-offset-4 hover:underline dark:text-slate-300">
          <FilterX size={16} aria-hidden="true" /> Alle filters wissen
        </button>
      ) : null}

      {body}

      {surprise ? (
        <SurpriseModal
          suggestion={surprise}
          onClose={() => setSurprise(null)}
          onAnother={anotherSurprise}
          onStart={(a) => {
            setSurprise(null);
            onStart(a);
          }}
        />
      ) : null}
    </section>
  );
}
